<template>
  <div>
    <h1>Companies</h1>
    <input
        id="search-input"
        v-model="search"
        class="search-input"
        name="search-input"
        placeholder="Search company (min 3 characters)..."
        type="text"
    />
    <div v-if="isLoading" class="loading">Searching...</div>
    <div v-else-if="error" class="error">Error: {{ error.message }}</div>
    <ul v-else class="company-list">
      <li v-for="company in companies" :key="company.id">
        <div class="company-item">
          <div class="company-name">{{ company.name }}</div>
          <div class="company-details">
            <span class="company-regno">{{ company.regNo }}</span>
            <span v-if="company.isDeletedAt" class="company-deleted">
              (Deleted: {{ new Date(company.isDeletedAt).toLocaleDateString() }})
            </span>
          </div>
        </div>
      </li>
      <li v-if="search.length > 0 && search.length < 3" class="no-results">
        Enter at least 3 characters to search.
      </li>
      <li v-if="search.length >= 3 && companies.length === 0" class="no-results">
        No companies found.
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import {ref, watch} from "vue";
import {searchCompanies} from "./api/companiesApi";
import type {CompanyDTO} from "./types";

defineOptions({
  name: "CompaniesAdmin",
});

const search = ref("");
const companies = ref<CompanyDTO[]>([]);
const isLoading = ref(false);
const error = ref<Error | null>(null);

// Debounce timer
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

watch(search, (newSearch) => {
  // Clear previous timer
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  // Reset state if search is too short
  if (newSearch.length < 3) {
    companies.value = [];
    error.value = null;
    return;
  }

  // Debounce search by 300ms
  debounceTimer = setTimeout(async () => {
    isLoading.value = true;
    error.value = null;

    try {
      let v = await searchCompanies(newSearch.trim());
      companies.value = v;
    } catch (e) {
      error.value = e as Error;
    } finally {
      isLoading.value = false;
    }
  }, 300);
});
</script>

<style scoped>
.admin-page {
  max-width: 400px;
  margin: 40px auto;
  padding: 2em;
  background: #fafbfc;
  border-radius: 6px;
  box-shadow: 0 3px 12px #0001;
}

h1 {
  font-size: 1.5em;
  margin-bottom: 1.2em;
}

.search-input {
  width: 100%;
  padding: 8px 10px;
  margin-bottom: 1em;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.company-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.company-list li {
  padding: 8px 12px;
  border-bottom: 1px solid #eee;
}

.company-list li:last-child {
  border-bottom: none;
}

.no-results {
  color: #888;
  padding: 1em 0;
  text-align: center;
}

.loading {
  color: #666;
  padding: 1em;
  text-align: center;
  font-style: italic;
}

.error {
  color: #d32f2f;
  padding: 1em;
  text-align: center;
  background: #ffebee;
  border-radius: 4px;
  margin-bottom: 1em;
}

.company-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.company-name {
  font-weight: 600;
  font-size: 1em;
}

.company-details {
  display: flex;
  gap: 8px;
  font-size: 0.875em;
  color: #666;
}

.company-regno {
  color: #555;
}

.company-deleted {
  color: #d32f2f;
  font-style: italic;
}
</style>
